import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import {trimValue} from '../utilities/utilities';

const
    // General settings
    settings = {
        templates: {
            backdrop: `<div class=\"modal-backdrop\"></div>`,
            modal: `<div class="modal">
    <button class="modal-close-button">
        <span></span>
        <span></span>
    </button>
    <div class="modal-content"></div>
</div>`,
        },

        open: {
            duration: 0.5,
            initialScale: {
                scaleUp: 0.9,
                scaleDown: 1.15,
            },
            easing: Power2.easeOut,
        },

        close: {
            duration: 0.5,
            endScale: {
                scaleUp: 1.1,
                scaleDown: 0.95,
            },
            easing: Power2.easeOut,
        },

        minimumTimeBeforeAllowingClose: 200,
        slowMoScale: 5,

        fadeAndPartialVerticalEnvelope: {
            envelopeAmount: 0.25,
        },

        // For fiddling with padding when the scroll bar disappears
        cssProperties: {
            // Padding from the desktop scroll bar
            modalScrollbarPadding: '--modal-scrollbar-padding',
            // Padding from the interior scroll bar within the modal
            // (if scrolling is enabled)
            modalInnerScrollbarPadding: '--modal-inner-scrollbar-padding',
        },
    };

const presentationTypes = {
    fade: 0,
    scaleUp: 1,
    scaleDown: 2,
    verticalEnvelope: 3,
    fadeAndPartialVerticalEnvelope: 4,
};

const openModals = [];

export default class Modal {
    static show(
        template,
        className,
        showPresentationType,
        closePresentationType,
        inhibitScrolling,
        immediate,
        callbacks,
        event,
    ) {
        const t = {
            timeline: null,

            // This monitors the <body> element for changes in padding
            // so that we can maintain the layout when the scroll bar
            // disappears; otherwise things shift horizontally when
            // scrolling is disabled
            scrollBarPadding: 0,
            mutationObserver: new MutationObserver(
                (mutationsList, observer) => {
                    // Check for a change in the padding on <body>
                    let newScrollBarPadding;
                    if (document.body.style.paddingRight) {
                        newScrollBarPadding = parseInt(
                            document.body.style.paddingRight,
                        );
                    } else {
                        newScrollBarPadding = 0;
                    }

                    if (newScrollBarPadding !== t.scrollBarPadding) {
                        // The padding on <body> has changed
                        t.scrollBarPadding = newScrollBarPadding;

                        if (t.scrollBarPadding > 0) {
                            document.body.style.setProperty(
                                settings.cssProperties.modalScrollbarPadding,
                                `${t.scrollBarPadding}px`,
                            );
                        } else {
                            document.body.style.removeProperty(
                                settings.cssProperties.modalScrollbarPadding,
                            );
                        }
                    }
                },
            ),

            close: function(immediate, presentationType, event) {
                if (t.timeline !== null) {
                    return false;
                }

                close.call(t, callbacks, immediate, presentationType, event);
                return false;
            },
        };

        // Remove tabindex values from everything
        const tabIndexElements = document.querySelectorAll('*[tabindex]');
        for (let i = 0; i < tabIndexElements.length; i++) {
            const tabIndexElement = tabIndexElements[i];
            const tabIndex = tabIndexElement.getAttribute('tabindex');

            // Skip elements already out of the tabindex flow
            if (parseInt(tabIndex) < 0) {
                continue;
            }

            // Store original tab index (this would not work right if we
            // were to allow more than one modal onscreen at a time, I think)
            tabIndexElement.setAttribute('data-original-tabindex', tabIndex);
            tabIndexElement.setAttribute('tabindex', '-1');
        }

        // Setup DOM elements
        t.backdrop = createElementFromTemplate(settings.templates.backdrop);
        t.container = createElementFromTemplate(settings.templates.modal);
        t.closeButton = t.container.querySelector('.modal-close-button');
        t.content = t.container.querySelector('.modal-content');

        if (className) {
            const classNames = className.split(/\s+/);
            classNames.forEach((className) => {
                t.container.classList.add(className);
            });
        }

        // Add the template content into the modal
        t.content.innerHTML = template;

        // Append elements to the end of the DOM
        t.backdrop.style.opacity = 0;
        document.body.appendChild(t.backdrop);

        t.container.style.opacity = 0;
        document.body.appendChild(t.container);

        // Make content scrollable if it's too tall to fit in the available space
        t.adjustContentHeight = function() {
            // Reset everything
            t.container.classList.remove('short-modal');

            t.container.style.top = '';
            t.container.style.height = '';
            t.container.style.transform = '';
            t.container.style.height = '';

            t.content.style.overflowY = '';
            t.content.style.height = 'auto';
            t.content.style.WebkitOverflowScrolling = 'touch';

            // Check if scrolling is enabled or not
            if (inhibitScrolling !== true) {
                // Scrolling is enabled - determine the size of the viewport
                // and the content container
                const availableHeight = t.container.clientHeight;
                const style = getComputedStyle(t.content);
                const contentHeight =
                    Math.floor(
                        t.content.offsetHeight +
                        parseInt(style.marginTop) +
                        parseInt(style.marginBottom),
                    ) - 1;

                if (contentHeight > availableHeight) {
                    // There isn't enough space in the viewport to display all the content -
                    // determine the size of the scrollable view
                    const
                        margin = contentHeight - t.content.clientHeight;
                    let scrollableViewHeight = availableHeight - margin;

                    // If the scrollable view is really narrow, adjust the positioning of the modal to maximize
                    // the amount of scrollable space onscreen - this is largely to accomodate mobile devices
                    // in landscape orientation, where the popup keyboard shrinks the available space to almost
                    // nothing
                    if (scrollableViewHeight < 100) {
                        scrollableViewHeight = window.innerHeight;
                        t.container.classList.add('short-modal');
                        immediate = true;
                    }

                    t.content.style.height = `${scrollableViewHeight}px`;
                    t.content.style.overflowY = 'scroll';
                    t.content.style.WebkitOverflowScrolling = 'touch';

                    t.includesScrollingContent = true;
                } else {
                    // There is enough room on screen to present the modal without a scrollbar -
                    // just set our flag
                    t.includesScrollingContent = false;
                }

                // Account for the width of the scroll bar by setting
                // a CSS property
                if (t.includesScrollingContent) {
                    const scrollBarPadding = (
                        t.container.clientWidth -
                        t.content.clientWidth
                    );

                    t.container.style.setProperty(
                        settings.cssProperties.modalInnerScrollbarPadding,
                        `${scrollBarPadding}px`,
                    );
                } else {
                    t.container.style.removeProperty(
                        settings.cssProperties.modalInnerScrollbarPadding,
                    );
                }
            } else {
                // Scrolling isn't enabled
                t.includesScrollingContent = false;
            }
        };

        window.addEventListener('resize', function() {
            t.adjustContentHeight();
        });

        // Setup events
        const
            openStartTime = (new Date()).getTime();

        const closeCallback = function(event) {
            event.preventDefault();
            event.stopPropagation();

                // Check if we're animating
            if (t.timeline !== null) {
                    // Check if the minimum amount of time has elapsed since starting
                    // the animation; if it hasn't, stop here
                const elapsed = (new Date()).getTime() - openStartTime;
                if (elapsed < settings.minimumTimeBeforeAllowingClose) {
                    return false;
                }
            }

                // Remove window events
            window.removeEventListener('keydown', escapeKeyCallback);

                // Run "cancel" callback, if specified
            if (callbacks) {
                if (typeof callbacks.cancel == 'function') {
                    callbacks.cancel.call(t);
                }
            }

            close.call(t, callbacks, false, closePresentationType, event);
        };

        const escapeKeyCallback = function(event) {
            if (event.key == 'Escape') {
                closeCallback(event);
            }
        };

        t.backdrop.addEventListener('click', closeCallback);
        t.closeButton.addEventListener('click', closeCallback);
        window.addEventListener('keydown', escapeKeyCallback);

        // Run "preShow" callback, if specified
        if (callbacks) {
            if (typeof callbacks.preShow == 'function') {
                callbacks.preShow.call(t);
            }
        }

        // Do this after preShow is called, in case that callback adjusts the
        // height of the modal's content
        t.adjustContentHeight();

        // Start our MutationObserver
        t.mutationObserver.observe(
            document.body,
            {
                attributeFilter: ['style'],
            },
        );

        // Fix content position
        disableBodyScroll(
            t.content,
            {
                reserveScrollBarGap: true,
            },
        );

        // Present modal
        if (immediate) {
            // Transition immediately
            t.backdrop.style.opacity = 1;
            t.container.style.opacity = 1;

            // Run "postShow" callback, if specified
            if (callbacks) {
                if (typeof callbacks.postShow == 'function') {
                    callbacks.postShow.call(t);
                }
            }
        } else {
            // Animate transition
            let
                duration = settings.open.duration;

            const progress = {
                percentComplete: 0,
            };

            const onCompleteCallback = function() {
                t.timeline = null;

                    // Run "postShow" callback, if specified
                if (callbacks) {
                    if (typeof callbacks.postShow == 'function') {
                        callbacks.postShow.call(t);
                    }
                }
            };

            // Scale duration if the shift key is pressed
            if (event) {
                if (event.shiftKey === true) {
                    duration *= settings.slowMoScale;
                }
            }

            // Create timeline
            t.timeline = new TimelineMax();

            switch (showPresentationType) {
                case presentationTypes.scaleUp:
                    t.timeline.to(
                        progress,
                        duration,
                        {
                            ease: settings.open.easing,
                            percentComplete: 1,

                            onUpdate: function() {
                                let scale =
                                    (1 - settings.open.initialScale.scaleUp) *
                                    progress.percentComplete;
                                scale += settings.open.initialScale.scaleUp;
                                scale = trimValue(scale);

                                t.backdrop
                                    .style
                                    .opacity = progress.percentComplete;
                                t.container
                                    .style
                                    .opacity = progress.percentComplete;
                                t.container
                                    .style
                                    .transform = `translate(-50%, -50%) scale(${scale})`;
                            },

                            onComplete: onCompleteCallback,
                        });
                    break;

                case presentationTypes.scaleDown:
                    t.timeline.to(
                        progress,
                        duration,
                        {
                            ease: settings.open.easing,
                            percentComplete: 1,

                            onUpdate: function() {
                                let scale =
                                    (1 - settings.open.initialScale.scaleDown) *
                                    progress.percentComplete;
                                scale += settings.open.initialScale.scaleDown;
                                scale = trimValue(scale);

                                t.backdrop
                                    .style
                                    .opacity = progress.percentComplete;
                                t.container
                                    .style
                                    .opacity = progress.percentComplete;
                                t.container
                                    .style
                                    .transform = `translate(-50%, -50%) scale(${scale})`;
                            },

                            onComplete: onCompleteCallback,
                        });
                    break;

                case presentationTypes.verticalEnvelope:
                    {
                        const containerHeight = t.container.clientHeight;

                        t.container.style.height = '0px';
                        t.container.style.opacity = 1;

                        t.timeline.to(
                            progress,
                            duration,
                            {
                                ease: settings.open.easing,
                                percentComplete: 1,

                                onUpdate: function() {
                                    const height =
                                        progress.percentComplete *
                                        containerHeight;

                                    // 2 = inner content remains still
                                    // > 2 = inner content shifts upward
                                    // < 2 = inner content shifts downward
                                    const offset =
                                        (height - containerHeight) /
                                        2.5;

                                    t.container
                                        .style
                                        .height = `${height}px`;
                                    t.content
                                        .style
                                        .top = `${offset}px`;

                                    t.closeButton
                                        .style
                                        .transform = `translate(0px, ${offset}px)`;

                                    t.backdrop
                                        .style
                                        .opacity = progress.percentComplete;
                                },

                                onComplete: function() {
                                    t.container.style.height = '';
                                    t.content.style.top = '';
                                    t.closeButton.style.transform = '';
                                    onCompleteCallback();
                                },
                            });
                    }
                    break;

                case presentationTypes.fadeAndPartialVerticalEnvelope:
                    {
                        // var containerHeight = t.container.clientHeight;
                        const containerHeight = t.content.clientHeight;

                        t.timeline.to(
                            progress,
                            duration,
                            {
                                ease: settings.open.easing,
                                percentComplete: 1,

                                onUpdate: function() {
                                    const envelopeAmount =
                                    settings
                                        .fadeAndPartialVerticalEnvelope
                                        .envelopeAmount;
                                    let height =
                                        (
                                            (1 - envelopeAmount) *
                                            containerHeight
                                        ) +
                                        (
                                            envelopeAmount *
                                            containerHeight *
                                            progress.percentComplete
                                        );
                                    height = trimValue(height);

                                    t.backdrop
                                        .style
                                        .opacity = progress.percentComplete;
                                    t.container
                                        .style
                                        .opacity = progress.percentComplete;
                                    t.content
                                        .style
                                        .height = `${height}px`;
                                },

                                onComplete: function() {
                                    t.content.style.height = '';
                                    onCompleteCallback();
                                },
                            });
                    }
                    break;

                // Default to fade transition
                default:
                    t.timeline.to(
                        progress,
                        duration,
                        {
                            ease: settings.open.easing,
                            percentComplete: 1,

                            onUpdate: function() {
                                t.backdrop
                                    .style
                                    .opacity = progress.percentComplete;
                                t.container
                                    .style
                                    .opacity = progress.percentComplete;
                            },

                            onComplete: onCompleteCallback,
                        });
                    break;
            }
        }

        // Add this modal to the reference array
        openModals.push(t);
    }

    static get numberOfOpenModals() {
        return openModals.length;
    }

    static get anyOpenModalContainsScrollingContent() {
        for (let i = 0; i < openModals.length; i++) {
            if (openModals[i].includesScrollingContent) {
                return true;
            }
        }

        return false;
    }

    static get presentationTypes() {
        return presentationTypes;
    }
}

/*
    Private methods
*/

/**
 * @this Modal
 * @param {object} callbacks
 * @param {boolean} immediate
 * @param {number} presentationType
 * @param {event} event
 */
function close(
    callbacks,
    immediate,
    presentationType,
    event,
) {
    const t = this;

    const restoreTabIndices = function() {
            // Remove tabindex values from everything
        const tabIndexElements = document.querySelectorAll('*[data-original-tabindex]');

        for (let i = 0; i < tabIndexElements.length; i++) {
            const tabIndexElement = tabIndexElements[i];
            const originalTabIndex = tabIndexElement.getAttribute('data-original-tabindex');

            tabIndexElement.setAttribute('tabindex', originalTabIndex);
            tabIndexElement.removeAttribute('data-original-tabindex');
        }
    };

    if (t.closing === true) {
        return;
    }

    t.closing = true;

    // Force immediate transition if we're in "short" mode
    if (t.container.classList.contains('short-modal')) {
        immediate = true;
    }

    // Stop any ongoing animation
    if (t.timeline) {
        t.timeline.progress(1);
    }

    // Run "preClose" callback, if specified
    if (callbacks) {
        if (typeof callbacks.preClose == 'function') {
            callbacks.preClose.call(t);
        }
    }

    // Adjust our positioning if we've fiddled with padding
    if (t.scrollBarPadding) {
        document.body.style.removeProperty(
            settings.cssProperties.modalScrollbarPadding,
        );
    }

    // Adjust our positioning if we've fiddled with scollbar padding
    if (t.scrollBarPadding) {
        this.container.style.left = `calc(50% + ${t.scrollBarPadding / 2}px)`;
    }

    // Unfix content position
    enableBodyScroll(t.content);

    // Disconnect our MutationObserver - this needs to happen
    // after a short delay, otherwise the adjustments made during
    // the "enableBodyScroll" call won't trigger the observer callback
    setTimeout(
        () => {
            t.mutationObserver.disconnect();
        },
        1,
    );

    if (immediate) {
        // Transition immediately
        t.backdrop.parentNode.removeChild(t.backdrop);
        t.container.parentNode.removeChild(t.container);

        // Run "postClose" callback, if specified
        if (callbacks) {
            if (typeof callbacks.postClose == 'function') {
                callbacks.postClose.call(t);
            }
        }

        // Restore tabindex values
        restoreTabIndices();
    } else {
        // Animate transition
        let duration = settings.close.duration;
        const progress = {
            percentComplete: 0,
        };

        const onCompleteCallback = function() {
            t.backdrop.parentNode.removeChild(t.backdrop);
            t.container.parentNode.removeChild(t.container);

            t.timeline = null;

                // Run "postClose" callback, if specified
            if (callbacks) {
                if (typeof callbacks.postClose == 'function') {
                    callbacks.postClose.call(t);
                }
            }

                // Restore tabindex values
            restoreTabIndices();
        };

        // Scale duration if the shift key is pressed
        if (event) {
            if (event.shiftKey === true) {
                duration *= settings.slowMoScale;
            }
        }

        // Create timeline
        t.timeline = new TimelineMax();

        switch (presentationType) {
            case presentationTypes.scaleUp:
                t.timeline.to(
                    progress,
                    duration,
                    {
                        ease: settings.close.easing,
                        percentComplete: 1,

                        onUpdate: function() {
                            let scale =
                                (settings.close.endScale.scaleUp - 1) *
                                progress.percentComplete;
                            scale += 1;
                            scale = trimValue(scale);

                            t.backdrop
                                .style
                                .opacity = 1 - progress.percentComplete;
                            t.container
                                .style
                                .opacity = 1 - progress.percentComplete;
                            t.container
                                .style
                                .transform = `translate(-50%, -50%) scale(${scale})`;
                        },

                        onComplete: onCompleteCallback,
                    });
                break;

            case presentationTypes.scaleDown:
                t.timeline.to(
                    progress,
                    duration,
                    {
                        ease: settings.close.easing,
                        percentComplete: 1,

                        onUpdate: function() {
                            let scale =
                                (settings.close.endScale.scaleDown - 1) *
                                progress.percentComplete;
                            scale += 1;
                            scale = trimValue(scale);

                            t.backdrop
                                .style
                                .opacity = 1 - progress.percentComplete;
                            t.container
                                .style
                                .opacity = 1 - progress.percentComplete;
                            t.container
                                .style
                                .transform = `translate(-50%, -50%) scale(${scale})`;
                        },

                        onComplete: onCompleteCallback,
                    });
                break;

            case presentationTypes.verticalEnvelope:
                const containerHeight = t.container.clientHeight;

                t.timeline.to(
                    progress,
                    duration,
                    {
                        ease: settings.close.easing,
                        percentComplete: 1,

                        onUpdate: function() {
                            const height =
                                (1 - progress.percentComplete) *
                                containerHeight;

                            // 2 = inner content remains still
                            // > 2 = inner content shifts upward
                            // < 2 = inner content shifts downward
                            const offset = (height - containerHeight) / 2.5;

                            t.container
                                .style
                                .height = `${height}px`;
                            t.content
                                .style
                                .top = `${offset}px`;

                            t.closeButton
                                .style
                                .transform = `translate(0px, ${offset}px)`;

                            t.backdrop
                                .style
                                .opacity = 1 - progress.percentComplete;
                        },

                        onComplete: function() {
                            t.container.style.height = '';
                            t.content.style.top = '';
                            t.closeButton.style.transform = '';

                            onCompleteCallback();
                        },
                    });
                break;

            // Default to fade transition
            default:
                t.timeline.to(
                    progress,
                    duration,
                    {
                        ease: settings.close.easing,
                        percentComplete: 1,

                        onUpdate: function() {
                            t.backdrop
                                .style
                                .opacity = 1 - progress.percentComplete;
                            t.container
                                .style
                                .opacity = 1 - progress.percentComplete;
                        },

                        onComplete: onCompleteCallback,
                    });
                break;
        }
    }

    // Remove this modal from the reference array
    for (let i = 0; i < openModals.length; i++) {
        if (openModals[i] !== t) {
            continue;
        }

        openModals.splice(i, 1);
        break;
    }
}

function createElementFromTemplate(template) {
    const element = document.createElement('div');
    element.innerHTML = template;

    return element.firstChild;
}
